import api from "./api";
import type {
  AgendaWeatherPayload,
  AgendaWeatherLocation,
  CityWeather,
  HourlyWeather,
  WeatherCondition,
  AgendaWeatherAlert,
} from "../pages/Schedules/agendaWeatherTypes";
import { normalizeAgendaIsoDate } from "../pages/Schedules/agendaFiltersQuery";

type LooseRecord = Record<string, unknown>;

function unwrapData<T>(response: { data?: { data?: T } | T }): T {
  const payload = response.data;
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

function readString(source: LooseRecord, ...keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string") {
      return value;
    }
  }
  return "";
}

function readNumber(source: LooseRecord, ...keys: string[]): number {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }
  return 0;
}

function readBoolean(source: LooseRecord, ...keys: string[]): boolean {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "boolean") {
      return value;
    }
  }
  return false;
}

function readArray(source: LooseRecord, ...keys: string[]): unknown[] {
  for (const key of keys) {
    const value = source[key];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
}

function mapHour(raw: LooseRecord): HourlyWeather {
  return {
    time: readString(raw, "time"),
    temperatureC: readNumber(raw, "temperature_c", "temperatureC"),
    rainChance: readNumber(raw, "rain_chance", "rainChance"),
    precipitationMm: readNumber(raw, "precipitation_mm", "precipitationMm"),
    condition: (readString(raw, "condition") || "unknown") as WeatherCondition,
    conditionLabel: readString(raw, "condition_label", "conditionLabel"),
  };
}

function mapSummary(raw: LooseRecord | null | undefined) {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const minTemperatureC = readNumber(raw, "min_temperature_c", "minTemperatureC");
  const maxTemperatureC = readNumber(raw, "max_temperature_c", "maxTemperatureC");
  const conditionLabel = readString(raw, "condition_label", "conditionLabel");

  if (!minTemperatureC && !maxTemperatureC && !conditionLabel) {
    return null;
  }

  return {
    minTemperatureC,
    maxTemperatureC,
    condition: (readString(raw, "condition") || "unknown") as WeatherCondition,
    conditionLabel,
    maxRainChance: readNumber(raw, "max_rain_chance", "maxRainChance"),
  };
}

function readOptionalBoolean(
  source: LooseRecord,
  ...keys: string[]
): boolean | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "boolean") {
      return value;
    }
  }
  return null;
}

function deriveSummaryFromHours(hours: HourlyWeather[]) {
  if (hours.length === 0) {
    return null;
  }

  let minTemperatureC = hours[0].temperatureC;
  let maxTemperatureC = hours[0].temperatureC;
  let maxRainChance = hours[0].rainChance;
  let conditionLabel = hours[0].conditionLabel;
  let condition = hours[0].condition;

  hours.forEach((hour) => {
    minTemperatureC = Math.min(minTemperatureC, hour.temperatureC);
    maxTemperatureC = Math.max(maxTemperatureC, hour.temperatureC);
    if (hour.rainChance >= maxRainChance) {
      maxRainChance = hour.rainChance;
      conditionLabel = hour.conditionLabel;
      condition = hour.condition;
    }
  });

  return {
    minTemperatureC,
    maxTemperatureC,
    condition,
    conditionLabel,
    maxRainChance,
  };
}

function mapCity(raw: LooseRecord): CityWeather {
  const hoursRaw = readArray(raw, "hours");
  const hours = hoursRaw
    .filter((entry): entry is LooseRecord => typeof entry === "object")
    .map(mapHour);

  const summaryRaw = raw.summary;
  let summary =
    summaryRaw && typeof summaryRaw === "object"
      ? mapSummary(summaryRaw as LooseRecord)
      : null;

  if (!summary && hours.length > 0) {
    summary = deriveSummaryFromHours(hours);
  }

  const explicitAvailable = readOptionalBoolean(raw, "available");
  const available =
    explicitAvailable ?? (summary != null || hours.length > 0);

  return {
    commercialRegionId: readNumber(
      raw,
      "commercial_region_id",
      "commercialRegionId"
    ),
    cityName: readString(raw, "city_name", "cityName"),
    date: readString(raw, "date"),
    available,
    observed: readOptionalBoolean(raw, "observed") ?? false,
    unavailableReason:
      readString(raw, "unavailable_reason", "unavailableReason") || null,
    summary,
    hours,
  };
}

function mapAlert(raw: LooseRecord): AgendaWeatherAlert {
  const scheduleIdsRaw = readArray(raw, "schedule_ids", "scheduleIds");

  return {
    commercialRegionId: readNumber(
      raw,
      "commercial_region_id",
      "commercialRegionId"
    ),
    cityName: readString(raw, "city_name", "cityName"),
    startTime: readString(raw, "start_time", "startTime"),
    endTime: readString(raw, "end_time", "endTime"),
    maxRainChance: readNumber(raw, "max_rain_chance", "maxRainChance"),
    severity: readString(raw, "severity") as AgendaWeatherAlert["severity"],
    condition: (readString(raw, "condition") || "unknown") as WeatherCondition,
    title: readString(raw, "title"),
    affectedVisits: readNumber(raw, "affected_visits", "affectedVisits"),
    affectedClients: readNumber(raw, "affected_clients", "affectedClients"),
    scheduleIds: scheduleIdsRaw.filter(
      (value): value is number => typeof value === "number"
    ),
  };
}

function mapLocation(raw?: LooseRecord | null): AgendaWeatherLocation | null {
  if (!raw) {
    return null;
  }

  return {
    teamId: readNumber(raw, "team_id", "teamId") || null,
    city: readString(raw, "city"),
    state: readString(raw, "state"),
    country: readString(raw, "country") || "Brazil",
    available: readBoolean(raw, "available"),
    unavailableReason:
      readString(raw, "unavailable_reason", "unavailableReason") || null,
    commercialRegionId:
      readNumber(raw, "commercial_region_id", "commercialRegionId") || null,
  };
}

export function mapAgendaWeatherPayload(raw: LooseRecord): AgendaWeatherPayload {
  const citiesRaw = readArray(raw, "cities");
  const alertsRaw = readArray(raw, "alerts");
  const locationRaw = raw.location;

  return {
    date: readString(raw, "date"),
    location:
      locationRaw && typeof locationRaw === "object"
        ? mapLocation(locationRaw as LooseRecord)
        : null,
    cities: citiesRaw
      .filter((entry): entry is LooseRecord => typeof entry === "object")
      .map(mapCity),
    alerts: alertsRaw
      .filter((entry): entry is LooseRecord => typeof entry === "object")
      .map(mapAlert),
    attribution:
      readString(raw, "attribution") ||
      "Dados meteorológicos: WeatherAPI.com",
  };
}

export type FetchAgendaWeatherParams = {
  date: string;
};

export async function fetchAgendaWeather(
  params: FetchAgendaWeatherParams,
  signal?: AbortSignal
): Promise<AgendaWeatherPayload> {
  const date = normalizeAgendaIsoDate(params.date) ?? params.date;

  const response = await api.get("/api/adm/schedules/weather", {
    params: { date },
    signal,
  });

  return mapAgendaWeatherPayload(unwrapData<LooseRecord>(response));
}
